/**
 * Lean Setup Wizard commit orchestrator.
 *
 * Core-100 R3 CORE-U — "a partially-created shop MUST NOT be left operable": the shop's operating structure
 * commits in ONE server transaction, `commit_shop_setup` (migration 20270206010100):
 *
 *   profile (name, time zone, hours + provenance, address) · technicians (the same rows
 *   create-technician-with-profile writes) · bays assigned to the technicians just created · the
 *   technician-side bay arrays · parking layout + spots · onboarding_state completed
 *
 * all or nothing, once per shop (a completed setup returns `alreadyCompleted` and writes nothing; concurrent
 * commits are serialised server-side). This replaced ~10 independent browser/Edge statements with a
 * best-effort client rollback that could not undo technician profiles, left parking behind, and could not
 * run at all once the tab closed.
 *
 * The browser still owns the PURE parts — hours defaulting, per-technician hours, the generated bay and
 * parking layouts — and sends their result. Two secondary steps keep their non-fatal contract server-side
 * (each in its own sub-transaction, reported in `degraded`). Invitations send email, so they run AFTER the
 * commit and never undo it; their outcomes are recorded on the onboarding state afterwards (best effort).
 *
 * Imported history (Slice 2+) feeds this same shape via a separate loader so the wizard component is
 * path-agnostic.
 */

import type { SupabaseClient } from '@supabase/supabase-js';


import type { Page1Data, Page2Data, Page3Data } from './wizardValidation';
import { generateBayLayout, type GeneratedBayPayload } from './generateBayLayout';
import { generateParkingLayout, onboardingParkingStartY } from './generateParkingLayout';
import {
  DEFAULT_SHOP_HOURS_MON_FRI_8_5,
  isHoursMapEmpty,
  type ShopHoursMap,
} from '@/lib/shopHours/defaults';

// ─── Day-key normalization ─────────────────────────────────────────────
//
// ShopBasicsStep stores hours keyed by lowercase day names
// ('monday'..'sunday'). `getTechDayHours` (src/utils/shopTime.ts:185-216)
// reads `tech.hours[String(dayIndex)]` OR `tech.hours[Capitalized]`
// where dayIndex is JS Date.getDay() (0=Sunday..6=Saturday). When the
// wizard passes its lowercase-keyed object directly into
// `technicians.hours`, every day lookup misses → tech filtered out of
// the Dayboard by useSortedTechnicians. Normalizing to numeric "0".."6"
// fixes the Dayboard filter AND any other consumer that uses
// getTechDayHours. We keep shop_profile.hours in its lowercase form
// elsewhere; this normalization only applies to the technician row's
// `hours` column.
const DAY_NAME_TO_NUMERIC: Record<string, string> = {
  sunday: '0',
  monday: '1',
  tuesday: '2',
  wednesday: '3',
  thursday: '4',
  friday: '5',
  saturday: '6',
};

function normalizeHoursKeysForTechnician(
  // Loose input type — accepts both Zod-emitted Page1Data.hours (where
  // inner `open`/`close` may be optional in the parsed shape's TS view)
  // and the wider lowercase-keyed editor state. The helper itself
  // filters out any incomplete days, so the output is always
  // strictly-typed.
  hours:
    | Record<string, { open?: string | null; close?: string | null } | undefined | null>
    | null
    | undefined,
): Record<string, { open: string; close: string }> {
  if (!hours || typeof hours !== 'object') return {};
  const out: Record<string, { open: string; close: string }> = {};
  for (const [rawKey, value] of Object.entries(hours)) {
    if (!value?.open || !value?.close) continue;
    const numericKey = DAY_NAME_TO_NUMERIC[String(rawKey).toLowerCase()] ?? rawKey;
    out[numericKey] = { open: value.open, close: value.close };
  }
  return out;
}

// ─── ONB-2 (K28): per-technician hours ────────────────────────────────
//
// Root cause of the defect: every wizard-created tech was written with
// `normalizeHoursKeysForTechnician(page1.hours)` — i.e. the SHOP's
// open/close broadcast to all techs — so the per-row Shift Start / Shift
// End the operator entered in Step 3 never reached `technicians.hours`,
// and the scheduler (getTechDayHours) treated every tech as working the
// shop's full bands.
//
// Fix: keep the set of working DAYS aligned with the shop's open days
// (the wizard rows carry no per-day off toggles — a day the shop is
// closed is a day the tech is off), but use the tech's OWN shift_start /
// shift_end as that day's open/close. When a row has no explicit shift
// (e.g. a non-default row that cleared both fields), fall back to the
// shop's hours so we never write an empty schedule for an open day.
export function buildTechnicianHours(
  shopHours:
    | Record<string, { open?: string | null; close?: string | null } | undefined | null>
    | null
    | undefined,
  shiftStart?: string | null,
  shiftEnd?: string | null,
): Record<string, { open: string; close: string }> {
  // Numeric-day map of the shop's OPEN days (with the shop's own
  // open/close as the fallback values).
  const openDays = normalizeHoursKeysForTechnician(shopHours);
  const start = typeof shiftStart === 'string' ? shiftStart.trim() : '';
  const end = typeof shiftEnd === 'string' ? shiftEnd.trim() : '';
  // No explicit per-tech shift → fall back to the shop hours verbatim.
  if (!start || !end) return openDays;
  const out: Record<string, { open: string; close: string }> = {};
  for (const dayKey of Object.keys(openDays)) {
    out[dayKey] = { open: start, close: end };
  }
  return out;
}


export interface CommitWizardInput {
  supabase: SupabaseClient;
  shopId: string;
  userId: string;
  page1: Page1Data;
  page2: Page2Data;
  page3: Page3Data;
  /**
   * Core-100 R4 (CORE-U, SP-05): where the structure came from. Omitted = typed by the person ('asserted'). An
   * import-prefilled setup passes its run and the inferred / confirmed field keys; the server refuses it unless
   * every inferred field was confirmed, and records the provenance with the setup act.
   */
  provenance?: {
    structure_source: 'asserted' | 'import_confirmed';
    import_run_id?: string | null;
    inferred_fields?: string[];
    confirmed_fields?: string[];
  };
  /**
   * Frontend origin used by `send-team-invitation` to build the join
   * link. The edge function itself prefers a server-side canonical URL
   * (APP_URL / PUBLIC_SITE_URL / LOVABLE_SITE_URL / SITE_URL) and falls
   * back to this value only when none of those are set and the supplied
   * origin is safe (not localhost / preview). LeanSetupWizard passes
   * `window.location.origin` here from the browser.
   */
  baseUrl?: string;
}

/**
 * One entry per attempted invite. Failures are NEVER fatal to the
 * commit (Batch 5 spec — setup completed but some invites failed should
 * not destroy the shop create). LeanSetupWizard surfaces these as a
 * follow-on toast so the operator can retry from Team Management.
 */
export interface CommitWizardInviteResult {
  email: string;
  team_role: string;
  ok: boolean;
  error?: string;
}

/**
 * A setup step that is non-fatal to the shop create but DID fail, so the caller can surface it instead of
 * silently swallowing it. The shop is still usable; only a secondary projection is degraded until fixed.
 */
export interface CommitWizardDegradedStep {
  step: 'tech_bay_sync' | 'parking_spot_positions' | 'technician_background';
  detail: string;
}

export interface CommitWizardResult {
  ok: boolean;
  technicianIds: string[];
  bayIds: string[];
  parkingLayoutId: string | null;
  parkingSpotCount: number;
  error?: string;
  /** Per-invite outcomes (Batch 5). Empty when no rows had Invite checked. */
  invites?: CommitWizardInviteResult[];
  /** True when the shop's setup was already completed: nothing was written. */
  alreadyCompleted?: boolean;
  /** Non-fatal secondary-step failures. Undefined when everything persisted. */
  degraded?: CommitWizardDegradedStep[];
}

interface CommitShopSetupResponse {
  ok: boolean;
  already_completed: boolean;
  technician_ids: Record<string, string>;
  bay_ids: string[];
  parking_layout_id: string | null;
  parking_spot_count: number;
  degraded: CommitWizardDegradedStep[];
}

const blankToNull = (v: string | undefined): string | null => {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
};

export async function commitWizard(input: CommitWizardInput): Promise<CommitWizardResult> {
  const { supabase, shopId, userId, page1, page2, page3, baseUrl } = input;
  const nothing = { technicianIds: [] as string[], bayIds: [] as string[], parkingLayoutId: null, parkingSpotCount: 0 };

  try {
    // ── The pure parts, computed here and sent as one payload ─────────────
    // Batch 8B: hours are not a blocking field; an empty map falls back to the canonical Mon-Fri 8-5 default,
    // labelled 'default' (a provider-owned source — public_listing / grounded_search — is never downgraded:
    // the server keeps it).
    const operatorTypedHours = !isHoursMapEmpty(page1.hours as ShopHoursMap);
    const hoursToWrite = operatorTypedHours
      ? (page1.hours as ShopHoursMap)
      : { ...DEFAULT_SHOP_HOURS_MON_FRI_8_5 };

    // Page 3 holds the full team; only TECHNICIAN rows become technicians. `idx` is the row's ORIGINAL index,
    // which generateBayLayout's __techIndex refers to.
    const technicians = page3.technicians.flatMap((tech, idx) => {
      if ((tech.team_role ?? 'TECHNICIAN') !== 'TECHNICIAN') return [];
      return [{
        idx,
        name: tech.name,
        skill_tier: tech.skill_tier,
        shift_start: tech.shift_start,
        shift_end: tech.shift_end,
        efficiency_percentage: tech.efficiency_percentage,
        efficiency_source: tech.efficiency_source ?? null,
        buffer_percentage: tech.buffer_percentage,
        hours: buildTechnicianHours(hoursToWrite, tech.shift_start, tech.shift_end),
        experience_level: tech.experience_level ?? null,
        years_professional: tech.years_professional ?? null,
      }];
    });

    const bayPayloads: GeneratedBayPayload[] = generateBayLayout({
      technicians: page3.technicians,
      specialties: page3.specialties,
    });
    const bays = bayPayloads.map((b) => ({
      name: b.name,
      bay_number: b.bay_number,
      bay_type: b.bay_type,
      bay_category: b.bay_category,
      position_x: b.position_x,
      position_y: b.position_y,
      width: b.width,
      height: b.height,
      is_active: b.is_active,
      tech_index: b.__techIndex ?? null,
    }));
    const parkingSpots = page3.parking_spot_count > 0
      ? generateParkingLayout(page3.parking_spot_count, { startY: onboardingParkingStartY(bayPayloads) })
      : [];

    const stepsCompleted = (techIds: Record<string, string>, invites: CommitWizardInviteResult[]) => ({
      page1: { name: page1.name, time_zone: page1.time_zone, hours_set: true },
      page2: { staff_counts: page2 },
      page3: {
        technician_count: Object.keys(techIds).length,
        specialty_count: page3.specialties.length,
        parking_spot_count: page3.parking_spot_count,
        team_members: page3.technicians.map((t, i) => {
          const memberEmail = t.email?.trim() ?? '';
          const attemptedInvite = !!t.invite && !!memberEmail;
          const inviteRow = attemptedInvite ? invites.find((r) => r.email === memberEmail) ?? null : null;
          const memberTeamRole = t.team_role ?? 'TECHNICIAN';
          return {
            team_role: memberTeamRole,
            name: t.name,
            email: memberEmail || null,
            invite_attempted: attemptedInvite,
            invite_sent: inviteRow?.ok ?? false,
            invite_error: inviteRow?.ok === false ? inviteRow.error ?? null : null,
            technician_id: memberTeamRole === 'TECHNICIAN' ? techIds[String(i)] ?? null : null,
          };
        }),
      },
    });

    // ── ONE transaction ──────────────────────────────────────────────────
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc('commit_shop_setup', {
      p_shop_id: shopId,
      p_payload: {
        profile: {
          name: page1.name,
          time_zone: page1.time_zone,
          hours: hoursToWrite,
          hours_source: operatorTypedHours ? 'manual' : 'default',
          street_address: blankToNull(page1.street_address),
          city: blankToNull(page1.city),
          state: blankToNull(page1.state),
          postal_code: blankToNull(page1.postal_code),
          country: blankToNull(page1.country) ?? 'US',
        },
        technicians,
        bays,
        parking_spots: parkingSpots,
        steps_completed: stepsCompleted({}, []),
        provenance: input.provenance ?? { structure_source: 'asserted' },
      },
    });
    if (error) {
      // Nothing was written: the transaction rolled back as a whole.
      return { ok: false, ...nothing, error: error.message };
    }
    const committed = data as CommitShopSetupResponse;
    if (committed.already_completed) {
      return { ok: true, alreadyCompleted: true, ...nothing, invites: [] };
    }
    const techIds = committed.technician_ids ?? {};

    // ── Invitations: email, so after the commit and never undoing it ─────
    const inviteResults: CommitWizardInviteResult[] = [];
    for (const [idx, tech] of page3.technicians.entries()) {
      const email = tech.email?.trim() ?? '';
      if (!tech.invite || !email) continue;
      const teamRole: string = tech.team_role ?? 'TECHNICIAN';
      const technicianId = teamRole === 'TECHNICIAN' ? techIds[String(idx)] : undefined;
      const authorityRole =
        ['OWNER', 'MANAGER', 'ORG_ADMIN', 'AUDITOR', 'REGION_MANAGER', 'DISTRICT_MANAGER']
          .includes(teamRole) ? teamRole : 'STAFF';
      const workRoles =
        teamRole === 'TECHNICIAN' ? ['TECHNICIAN']
        // WS-ELIG B-E7: detail work; the roster entry is created on acceptance (work-role driven).
        : teamRole === 'DETAIL_SPECIALIST' ? ['DETAIL_SPECIALIST']
        : teamRole === 'SERVICE_WRITER' ? ['SERVICE_ADVISOR']
        : teamRole === 'DISPATCHER' ? ['DISPATCHER']
        : teamRole === 'PARTS' ? ['PARTS']
        : [];
      try {
        const { error: inviteError } = await supabase.functions.invoke('send-team-invitation', {
          body: { shopId, email, authorityRole, workRoles, shopName: page1.name, baseUrl, technicianId },
        });
        if (inviteError) {
          inviteResults.push({ email, team_role: teamRole, ok: false, error: inviteError.message ?? 'Invitation failed' });
          console.warn('[commitWizard] send-team-invitation failed (non-fatal):', inviteError, { teamRole });
        } else {
          inviteResults.push({ email, team_role: teamRole, ok: true });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        inviteResults.push({ email, team_role: teamRole, ok: false, error: msg });
        console.warn('[commitWizard] send-team-invitation threw (non-fatal):', e, { teamRole });
      }
    }

    // Record the invite outcomes and created technician ids on the (already completed) state. Best effort: the
    // setup itself is committed regardless.
    const { error: stateErr } = await supabase
      .from('onboarding_state')
      .update({ steps_completed: stepsCompleted(techIds, inviteResults), last_page: 'page3' })
      .eq('user_id', userId)
      .eq('shop_id', shopId)
      .eq('flow', 'setup_wizard');
    if (stateErr) {
      console.warn('[commitWizard] onboarding_state invite outcomes not recorded (non-fatal):', stateErr);
    }

    const degraded = committed.degraded ?? [];
    return {
      ok: true,
      technicianIds: Object.values(techIds),
      bayIds: committed.bay_ids ?? [],
      parkingLayoutId: committed.parking_layout_id ?? null,
      parkingSpotCount: committed.parking_spot_count ?? 0,
      invites: inviteResults,
      degraded: degraded.length > 0 ? degraded : undefined,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, ...nothing, error: msg };
  }
}
